/**
 * TaskQuickAdd Component
 * Always-visible text input at the top of the sidebar for quick task creation.
 * Includes an inline date chip picker, @mention assignment, #category selection,
 * NLP date parsing (amber chips), and smart category suggestion.
 *
 * On focus the input expands rightward over the calendar (fixed position)
 * with a smooth animation, giving more room for natural language input.
 */

import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, Sparkles } from 'lucide-react';
import { TaskSource } from '@signalvote/shared';
import type { ParsedDateResult } from '@signalvote/shared';
import { parseDateFromText } from '@signalvote/shared';
import { useCreateTask, useTaskCategories, useSuggestCategory } from '@/hooks/queries';
import { getCampaignToday } from '@/lib/calendarUtils';
import { DateChipPicker } from './DateChipPicker';
import { MentionPopover } from './MentionPopover';
import type { MentionPopoverRef } from './MentionPopover';
import { CategoryPopover } from './CategoryPopover';
import type { CategoryPopoverRef } from './CategoryPopover';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

interface Assignee {
  id: string;
  displayName: string;
}

interface SelectedCategory {
  id: string;
  name: string;
}

type DateSource = 'inline' | 'picker' | null;

// ============================================================================
// Constants
// ============================================================================

const PLACEHOLDER_EXAMPLES = [
  'Try: call voters tomorrow at 3pm',
  'Try: door knock Saturday #fieldwork',
  'Try: follow up next week @Sarah',
  'Try: prep materials Friday morning',
];

const PLACEHOLDER_INTERVAL_MS = 4000;
const DEFAULT_PLACEHOLDER = 'Add a task... @ assign, # category';
const EXPANDED_WIDTH = 700;

// ============================================================================
// Component
// ============================================================================

export function TaskQuickAdd() {
  const [title, setTitle] = useState('');
  const [selectedDate, setSelectedDate] = useState<string | undefined>();
  const [selectedTime, setSelectedTime] = useState<string | undefined>();
  const [assignee, setAssignee] = useState<Assignee | null>(null);
  const [mentionActive, setMentionActive] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIdx, setMentionStartIdx] = useState(-1);
  const [categoryActive, setCategoryActive] = useState(false);
  const [categoryQuery, setCategoryQuery] = useState('');
  const [categoryStartIdx, setCategoryStartIdx] = useState(-1);
  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory | null>(null);
  const [dateSegment, setDateSegment] = useState<ParsedDateResult | null>(null);
  const [dateSource, setDateSource] = useState<DateSource>(null);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const mentionRef = useRef<MentionPopoverRef>(null);
  const categoryRef = useRef<CategoryPopoverRef>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);

  const createTask = useCreateTask();

  // ---- Category suggestion ----

  const { data: categories } = useTaskCategories();
  const categoryInputs = (categories ?? []).map((c) => ({ id: c.id, name: c.name }));

  const { suggestedCategory } = useSuggestCategory(
    title,
    categoryInputs,
    !selectedCategory && !suggestionDismissed
  );

  // ---- Rotating placeholder ----

  useEffect(() => {
    if (title) return;
    const timer = setInterval(() => {
      setPlaceholderIdx((i) => (i + 1) % PLACEHOLDER_EXAMPLES.length);
    }, PLACEHOLDER_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [title]);

  const placeholder = title ? DEFAULT_PLACEHOLDER : PLACEHOLDER_EXAMPLES[placeholderIdx];

  // ---- Expand / Collapse ----

  function expand(): void {
    if (isExpanded) return;
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) setAnchorRect(rect);
    setIsExpanded(true);
  }

  function collapse(): void {
    if (!isExpanded) return;
    setIsExpanded(false);
  }

  // Measure anchor position before paint so the portal appears in the right spot
  useLayoutEffect(() => {
    if (isExpanded && anchorRef.current) {
      setAnchorRect(anchorRef.current.getBoundingClientRect());
    }
  }, [isExpanded]);

  // Focus the input after expanding
  useEffect(() => {
    if (isExpanded) {
      // Small delay to allow the portal to mount
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isExpanded]);

  // Close on Escape when nothing else is capturing it
  useEffect(() => {
    if (!isExpanded) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape' && !mentionActive && !categoryActive) {
        collapse();
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded, mentionActive, categoryActive]);

  // ---- Tag helpers ----

  const mentionTag = assignee ? `@${assignee.displayName}` : '';
  const categoryTag = selectedCategory ? `#${selectedCategory.name}` : '';

  function resetMentionState(): void {
    setMentionActive(false);
    setMentionQuery('');
    setMentionStartIdx(-1);
  }

  function resetCategoryState(): void {
    setCategoryActive(false);
    setCategoryQuery('');
    setCategoryStartIdx(-1);
  }

  function resetAllState(): void {
    setTitle('');
    setSelectedDate(undefined);
    setSelectedTime(undefined);
    setAssignee(null);
    resetMentionState();
    setSelectedCategory(null);
    resetCategoryState();
    setDateSegment(null);
    setDateSource(null);
    setSuggestionDismissed(false);
  }

  // ---- NLP date parsing ----

  const runDateParsing = useCallback(
    (text: string) => {
      if (mentionActive || categoryActive) return;

      const excludeRanges: Array<{ start: number; end: number }> = [];
      if (assignee) {
        const mIdx = text.indexOf(mentionTag);
        if (mIdx >= 0) excludeRanges.push({ start: mIdx, end: mIdx + mentionTag.length });
      }
      if (selectedCategory) {
        const cIdx = text.indexOf(categoryTag);
        if (cIdx >= 0) excludeRanges.push({ start: cIdx, end: cIdx + categoryTag.length });
      }

      const result = parseDateFromText(text, getCampaignToday(), excludeRanges);

      if (result && dateSource !== 'picker') {
        setDateSegment(result);
        setSelectedDate(result.date);
        setSelectedTime(result.time);
        setDateSource('inline');
      } else if (!result && dateSource === 'inline') {
        setDateSegment(null);
        setSelectedDate(undefined);
        setSelectedTime(undefined);
        setDateSource(null);
      }
    },
    [mentionActive, categoryActive, assignee, selectedCategory, mentionTag, categoryTag, dateSource]
  );

  // ---- Input handling ----

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart ?? newValue.length;

    if (assignee && !newValue.includes(mentionTag)) {
      setAssignee(null);
      setTitle(newValue);
      runDateParsing(newValue);
      return;
    }

    if (selectedCategory && !newValue.includes(categoryTag)) {
      setSelectedCategory(null);
      setSuggestionDismissed(false);
      setTitle(newValue);
      runDateParsing(newValue);
      return;
    }

    setTitle(newValue);

    if (!assignee) {
      const lastAt = newValue.lastIndexOf('@', cursorPos - 1);
      if (lastAt >= 0 && (lastAt === 0 || newValue[lastAt - 1] === ' ')) {
        setMentionActive(true);
        setMentionStartIdx(lastAt);
        setMentionQuery(newValue.slice(lastAt + 1, cursorPos));
        resetCategoryState();
        return;
      } else if (mentionActive) {
        resetMentionState();
      }
    }

    if (!selectedCategory) {
      const lastHash = newValue.lastIndexOf('#', cursorPos - 1);
      if (lastHash >= 0 && (lastHash === 0 || newValue[lastHash - 1] === ' ')) {
        setCategoryActive(true);
        setCategoryStartIdx(lastHash);
        setCategoryQuery(newValue.slice(lastHash + 1, cursorPos));
        resetMentionState();
        return;
      } else if (categoryActive) {
        resetCategoryState();
      }
    }

    runDateParsing(newValue);
  }

  function handleMentionSelect(userId: string, displayName: string): void {
    const before = title.slice(0, mentionStartIdx);
    const afterAt = title.indexOf(' ', mentionStartIdx);
    const after = afterAt >= 0 ? title.slice(afterAt) : '';
    const tag = `@${displayName}`;

    setTitle(`${before}${tag}${after || ' '}`);
    setAssignee({ id: userId, displayName });
    resetMentionState();
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleCategorySelect(categoryId: string, categoryName: string): void {
    const before = title.slice(0, categoryStartIdx);
    const afterHash = title.indexOf(' ', categoryStartIdx);
    const after = afterHash >= 0 ? title.slice(afterHash) : '';
    const tag = `#${categoryName}`;

    setTitle(`${before}${tag}${after || ' '}`);
    setSelectedCategory({ id: categoryId, name: categoryName });
    setSuggestionDismissed(false);
    resetCategoryState();
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleSuggestionAccept(): void {
    if (!suggestedCategory) return;
    setSelectedCategory({ id: suggestedCategory.id, name: suggestedCategory.name });
    setTitle((prev) => `${prev.trimEnd()} #${suggestedCategory.name} `);
    setSuggestionDismissed(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleSuggestionDismiss(): void {
    setSuggestionDismissed(true);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (mentionActive) {
      if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) {
        mentionRef.current?.handleKeyDown(e);
        return;
      }
    }

    if (categoryActive) {
      if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) {
        categoryRef.current?.handleKeyDown(e);
        return;
      }
    }

    if (e.key === 'Escape' && isExpanded) {
      e.preventDefault();
      collapse();
      return;
    }

    if (e.key === 'Enter' && title.trim()) {
      e.preventDefault();
      let cleanTitle = title;

      if (assignee) {
        cleanTitle = cleanTitle.replace(mentionTag, '');
      }
      if (selectedCategory) {
        cleanTitle = cleanTitle.replace(categoryTag, '');
      }
      if (dateSegment && dateSource === 'inline') {
        cleanTitle = cleanTitle.replace(dateSegment.text, '');
      }
      cleanTitle = cleanTitle.replace(/\s+/g, ' ').trim();

      if (!cleanTitle) return;

      const finalCategory = selectedCategory ?? (suggestedCategory && !suggestionDismissed ? suggestedCategory : null);

      createTask.mutate({
        title: cleanTitle,
        description: '',
        source: TaskSource.ManagerAssigned,
        dueDate: selectedDate,
        dueTime: selectedTime,
        assignedToId: assignee?.id,
        assignedToName: assignee?.displayName,
        categoryId: finalCategory?.id,
        categoryName: finalCategory?.name,
      }, {
        onError: () => {
          setTitle(cleanTitle);
          setCreateError(`Couldn't add "${cleanTitle}". Try again.`);
        },
      });
      resetAllState();
      setCreateError(null);
      collapse();
    }
  }

  function handleDateSelect(date?: string, time?: string): void {
    setSelectedDate(date);
    setSelectedTime(time);
    setDateSource('picker');
    setDateSegment(null);
  }

  function handleDateClear(): void {
    setSelectedDate(undefined);
    setSelectedTime(undefined);
    setDateSegment(null);
    setDateSource(null);
  }

  // ---- Overlay rendering ----

  const hasAssigneeInTitle = assignee && title.includes(mentionTag);
  const hasCategoryInTitle = selectedCategory && title.includes(categoryTag);
  const hasDateInTitle = dateSegment && dateSource === 'inline';
  // Only show the overlay in expanded mode — collapsed is too narrow and collides with DateChipPicker
  const hasOverlay = isExpanded && (hasAssigneeInTitle || hasCategoryInTitle || hasDateInTitle);

  function renderCombinedOverlay(): React.ReactNode {
    type QuickAddSegment = { text: string; type: 'normal' | 'mention' | 'category' | 'date' };
    const segments: QuickAddSegment[] = [];

    const mentionIdx = hasAssigneeInTitle ? title.indexOf(mentionTag) : -1;
    const categoryIdx = hasCategoryInTitle ? title.indexOf(categoryTag) : -1;

    const tags: { idx: number; len: number; type: 'mention' | 'category' | 'date' }[] = [];
    if (mentionIdx >= 0) tags.push({ idx: mentionIdx, len: mentionTag.length, type: 'mention' });
    if (categoryIdx >= 0) tags.push({ idx: categoryIdx, len: categoryTag.length, type: 'category' });
    if (hasDateInTitle && dateSegment) {
      tags.push({ idx: dateSegment.index, len: dateSegment.text.length, type: 'date' });
    }
    tags.sort((a, b) => a.idx - b.idx);

    let cursor = 0;
    for (const { idx, len, type } of tags) {
      if (idx > cursor) {
        segments.push({ text: title.slice(cursor, idx), type: 'normal' });
      }
      segments.push({ text: title.slice(idx, idx + len), type });
      cursor = idx + len;
    }
    if (cursor < title.length) {
      segments.push({ text: title.slice(cursor), type: 'normal' });
    }

    return (
      <div
        className={cn(
          'absolute inset-0 pl-10 pr-10 pointer-events-none overflow-hidden whitespace-pre',
          isExpanded ? 'text-base leading-[44px]' : 'text-sm leading-[36px]'
        )}
      >
        {segments.map((seg, i) => {
          if (seg.type === 'mention') {
            return <span key={i} className="bg-blue-100 text-blue-700 rounded-sm">{seg.text}</span>;
          }
          if (seg.type === 'category') {
            return <span key={i} className="bg-emerald-100 text-emerald-700 rounded-sm">{seg.text}</span>;
          }
          if (seg.type === 'date') {
            return <span key={i} className="bg-amber-100 text-amber-700 rounded-sm">{seg.text}</span>;
          }
          return <span key={i} className="text-foreground">{seg.text}</span>;
        })}
      </div>
    );
  }

  // ---- Suggestion chip ----

  const showSuggestion = suggestedCategory && !selectedCategory && !suggestionDismissed && title.trim().length > 0;

  // ---- Render the input card (shared between inline and expanded) ----

  function renderInputCard(expanded: boolean): React.ReactNode {
    return (
      <div
        ref={expanded ? expandedRef : undefined}
        className={cn(
          'bg-background rounded-lg',
          expanded && 'shadow-lg border border-border ring-1 ring-ring/10'
        )}
      >
        <div className="relative flex items-center">
          <Plus className="absolute left-3 h-5 w-5 text-muted-foreground z-10" />
          <input
            ref={inputRef}
            data-coachmark="quick-add-task"
            type="text"
            placeholder={placeholder}
            value={title}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (!isExpanded) expand(); }}
            className={cn(
              'w-full pl-10 pr-10 text-sm rounded-md bg-background',
              'placeholder:text-muted-foreground',
              'focus:outline-none',
              expanded
                ? 'h-11 text-base border-0 bg-transparent'
                : 'h-9 border border-input hover:bg-accent/50 transition-colors focus:ring-1 focus:ring-ring focus:bg-card',
              hasOverlay && 'text-transparent caret-foreground'
            )}
          />
          {hasOverlay && renderCombinedOverlay()}

          <div className="absolute right-2 flex items-center gap-1 z-10">
            <DateChipPicker
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              onSelect={handleDateSelect}
              onClear={handleDateClear}
            />
          </div>

          <MentionPopover
            ref={mentionRef}
            open={mentionActive}
            onOpenChange={(open) => {
              if (!open) resetMentionState();
            }}
            onSelect={handleMentionSelect}
            searchQuery={mentionQuery}
            anchorRef={inputRef}
          />

          <CategoryPopover
            ref={categoryRef}
            open={categoryActive}
            onOpenChange={(open) => {
              if (!open) resetCategoryState();
            }}
            onSelect={handleCategorySelect}
            searchQuery={categoryQuery}
            anchorRef={inputRef}
          />
        </div>

        {/* Bottom bar: chips summary + suggestion */}
        {expanded && (
          <div className="px-3 pb-2 flex items-center gap-2 flex-wrap">
            {/* Show active chips as a summary row */}
            {assignee && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs">
                @{assignee.displayName}
              </span>
            )}
            {selectedCategory && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs">
                #{selectedCategory.name}
              </span>
            )}
            {selectedDate && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs">
                {selectedDate}{selectedTime ? ` ${selectedTime}` : ''}
              </span>
            )}

            {/* Category suggestion */}
            {showSuggestion && (
              <div className="inline-flex items-center gap-1 animate-in fade-in duration-200">
                <button
                  type="button"
                  onClick={handleSuggestionAccept}
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full',
                    'text-xs text-muted-foreground bg-muted/50',
                    'hover:bg-muted hover:text-foreground transition-colors cursor-pointer'
                  )}
                >
                  <Sparkles className="h-3 w-3" />
                  <span>#{suggestedCategory!.name}</span>
                </button>
                <button
                  type="button"
                  onClick={handleSuggestionDismiss}
                  className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                  aria-label="Dismiss suggestion"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* Hint text when nothing is active */}
            {!assignee && !selectedCategory && !selectedDate && !showSuggestion && (
              <span className="text-xs text-muted-foreground/60">
                @ assign &middot; # category &middot; dates auto-detected
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // ---- Layout ----

  return (
    <>
      {/* Anchor — always in the sidebar. Invisible placeholder when expanded. */}
      <div ref={anchorRef} className="relative">
        {!isExpanded && renderInputCard(false)}
        {!isExpanded && createError && (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {createError}
          </p>
        )}
        {isExpanded && (
          // Invisible placeholder to hold sidebar space
          <div className="h-9 rounded-md border border-input bg-muted/30" />
        )}

        {/* Non-expanded suggestion chip (below input in sidebar) */}
        {!isExpanded && showSuggestion && (
          <div className="flex items-center gap-1.5 mt-1 ml-10 animate-in fade-in duration-200">
            <button
              type="button"
              onClick={() => { expand(); handleSuggestionAccept(); }}
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-md',
                'text-xs text-muted-foreground bg-muted/50',
                'hover:bg-muted hover:text-foreground transition-colors cursor-pointer'
              )}
            >
              <Sparkles className="h-3 w-3" />
              <span>Suggested: #{suggestedCategory!.name}</span>
            </button>
            <button
              type="button"
              onClick={handleSuggestionDismiss}
              className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
              aria-label="Dismiss suggestion"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      {/* Expanded overlay — portaled to body so it can escape the sidebar overflow */}
      {isExpanded && anchorRect && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={collapse}
          />

          {/* Floating expanded input */}
          <div
            className="fixed z-50 animate-in fade-in slide-in-from-top-1 duration-150"
            style={{
              top: anchorRect.top,
              left: anchorRect.left,
              width: EXPANDED_WIDTH,
            }}
          >
            {renderInputCard(true)}
          </div>
        </>,
        document.body
      )}
    </>
  );
}
